import { Controller } from "@hotwired/stimulus"

// Phase 2.7 Tier 3 — drag-to-reorder topic admin.
//
// Native HTML5 drag-and-drop reordering of <tr data-topic-id="…">
// rows. On drop, POSTs the new order to the controller's reorder
// endpoint as a JSON `{ topic_ids: [...] }` payload. Position is
// persisted server-side; the UI updates the visual order
// optimistically before the response.
//
// Mirrors the lightweight DnD pattern used by
// `kanban_controller.js` and avoids pulling in an external DnD
// library (Sortable.js / react-dnd) — the surface here is small.
export default class extends Controller {
  static targets = ["list", "row"]
  static values = { reorderUrl: String, csrf: String }

  connect() {
    this._draggingRow = null
    // Stimulus calls `rowTargetConnected` for every existing target on
    // controller connect, so wiring lives there to avoid double-binds.
  }

  rowTargetConnected(row) {
    this._wireRow(row)
  }

  _wireRow(row) {
    if (row.dataset.dragWired === "true") return
    row.dataset.dragWired = "true"
    row.addEventListener("dragstart", (e) => this._onDragStart(e, row))
    row.addEventListener("dragover", (e) => this._onDragOver(e, row))
    row.addEventListener("drop", (e) => this._onDrop(e, row))
    row.addEventListener("dragend", () => this._onDragEnd(row))
  }

  _onDragStart(event, row) {
    this._draggingRow = row
    row.classList.add("opacity-40", "bg-blue-50")
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", row.dataset.topicId)
    }
  }

  _onDragOver(event, row) {
    if (!this._draggingRow || row === this._draggingRow) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
    row.classList.add("border-t-2", "border-brand")
  }

  _onDrop(event, row) {
    event.preventDefault()
    row.classList.remove("border-t-2", "border-brand")
    if (!this._draggingRow || row === this._draggingRow) return

    const list = this.listTarget
    const dragged = this._draggingRow
    const { top: rowTop, height: rowHeight } = row.getBoundingClientRect()
    const dropAbove = event.clientY < rowTop + rowHeight / 2
    list.insertBefore(dragged, dropAbove ? row : row.nextSibling)
    this._persistOrder()
  }

  _onDragEnd(row) {
    row.classList.remove("opacity-40", "bg-blue-50")
    this.rowTargets.forEach((r) => r.classList.remove("border-t-2", "border-brand"))
    this._draggingRow = null
  }

  _persistOrder() {
    const ids = this.rowTargets.map((r) => r.dataset.topicId).filter(Boolean)
    if (!ids.length || !this.hasReorderUrlValue) return

    const formData = new FormData()
    ids.forEach((id) => formData.append("topic_ids[]", id))

    fetch(this.reorderUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfValue || this._csrfFallback(),
        "Accept": "application/json"
      },
      credentials: "same-origin",
      body: formData
    }).catch((err) => {
      console.warn("[meeting-topic-admin] reorder failed:", err)
    })
  }

  _csrfFallback() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
